import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Button, Card, Icon, Input, SegmentedControl } from '@astral/ui';
import {
  STICKER_TYPES,
  writeProjectCategory,
  type BuiltInCategory,
  type ProjectCategoryRef,
  type StickerType,
} from '@astral/shared';
import { hapticImpact } from '@astral/telegram/webapp';
import { api, ApiClientError } from '../lib/api.js';
import { membershipErrorKey } from '../lib/api-errors.js';
import { CategoryPicker } from '../components/CategoryPicker.js';
import { useCustomCategories } from '../hooks/useCustomCategories.js';
import { ASTRAL_CHANNEL_USERNAME } from '../config.js';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';
import { useUsage } from '../hooks/useUsage.js';

/** Minimal project creation: name, category, sticker type (Phase 11). */
export function CreateProjectScreen(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { forType } = useUsage();

  const [title, setTitle] = useState('');
  /*
   * NO DEFAULT. A new project starts uncategorised — it used to default to
   * "other", which is why so many existing rows carry that value.
   */
  const [category, setCategory] = useState<ProjectCategoryRef>({ kind: 'none' });
  const { categories: customCategories } = useCustomCategories();
  const [stickerType, setStickerType] = useState<StickerType>('IMAGE');
  const [creating, setCreating] = useState(false);

  const usage = forType(stickerType);
  const blocked = usage?.limit != null && (usage.remaining ?? 0) <= 0;

  const create = async (): Promise<void> => {
    if (title.trim().length === 0 || blocked) return;
    setCreating(true);
    try {
      // The picker never produces a legacy value, so this is a built-in id.
      const columns = writeProjectCategory(category) as {
        category: BuiltInCategory | null;
        customCategoryId: string | null;
      };
      const project = await api.createProject({
        title: title.trim(),
        stickerType,
        // Only send a field that is actually set; the schema rejects nulls here.
        ...(columns.category ? { category: columns.category } : {}),
        ...(columns.customCategoryId ? { customCategoryId: columns.customCategoryId } : {}),
      });
      hapticImpact('medium');
      toast.show(t('toast.created'), 'success');
      navigate(`/projects/${project.id}`, { replace: true });
    } catch (err) {
      // Membership refusals get their own copy: "join the channel" and "we
      // couldn't check" are different problems with different next steps.
      const membership = membershipErrorKey(err);
      if (membership) {
        toast.show(t(membership as TranslationKey, { channel: `@${ASTRAL_CHANNEL_USERNAME}` }), 'error');
      } else if (err instanceof ApiClientError && err.code === 'LIMIT_REACHED') {
        toast.show(t('usage.limitTitle'), 'error');
      } else {
        toast.show(t('errors.generic'), 'error');
      }
      setCreating(false);
    }
  };

  return (
    <div className="app-root">
      <AppBar title={t('projectForm.createTitle')} onBack={() => navigate(-1)} />
      <div className="screen">
        <Input
          label={t('projectForm.titleLabel')}
          placeholder={t('projectForm.titlePlaceholder')}
          value={title}
          maxLength={80}
          autoFocus
          onChange={(event) => setTitle(event.target.value)}
        />

        <div className="stack" style={{ gap: 8 }}>
          <span className="as-field__label">{t('projectForm.categoryLabel')}</span>
          <div className="chips">
            <CategoryPicker
              category={writeProjectCategory(category).category}
              customCategoryId={writeProjectCategory(category).customCategoryId}
              custom={customCategories}
              onChange={setCategory}
            />
          </div>
        </div>

        <div className="stack" style={{ gap: 8 }}>
          <span className="as-field__label">{t('projectForm.typeLabel')}</span>
          <SegmentedControl<StickerType>
            value={stickerType}
            onChange={setStickerType}
            options={STICKER_TYPES.map((value) => ({
              value,
              label: t(`stickerTypes.${value}` as TranslationKey),
            }))}
          />
          {usage && (
            <span className="muted" style={{ fontSize: 12 }}>
              {usage.limit == null
                ? t('usage.unlimited')
                : t('usage.packs', { used: usage.used, limit: usage.limit })}
            </span>
          )}
        </div>

        {blocked ? (
          <Card padding="lg" className="paywall border-glow stack" style={{ gap: 12, alignItems: 'center', textAlign: 'center' }}>
            <span className="paywall__icon">
              <Icon name="sparkles" size={26} />
            </span>
            <strong>{t('usage.limitTitle')}</strong>
            <span className="muted">
              {t('usage.limitBody', { type: t(`stickerTypes.${stickerType}` as TranslationKey) })}
            </span>
            <span className="muted" style={{ fontSize: 13 }}>{t('premium.upgradeHint')}</span>
            <Button fullWidth leftIcon={<Icon name="sparkles" size={18} />} onClick={() => navigate('/premium')}>
              {t('premium.view')}
            </Button>
          </Card>
        ) : (
          <Button
            size="lg"
            fullWidth
            loading={creating}
            disabled={title.trim().length === 0}
            leftIcon={<Icon name="sparkles" size={18} />}
            onClick={() => void create()}
          >
            {t('projectForm.create')}
          </Button>
        )}
      </div>
    </div>
  );
}
